import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { User } from '../shared/models/user.model';
import { UserService } from '../core/services/user.service';
import { withEntitiesManagement } from './features/entities-management.feature';
import { inject } from '@angular/core';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
} from '@angular-architects/ngrx-toolkit';
import { SnackbarService } from '../core/services/snackbar.service';
import {
  UserStatusStatistics,
  UserSubscriptionStatistics,
} from '../shared/models/statistics.model';

export interface UsersState {
  userStatusStatistics: UserStatusStatistics[];
  userSubscriptionStatistics: UserSubscriptionStatistics[];
}

const initialUsersState: UsersState = {
  userStatusStatistics: [],
  userSubscriptionStatistics: [],
};

export const UsersStore = signalStore(
  { providedIn: 'root' },
  withEntitiesManagement<User>(UserService, 'users', 'users'),
  withState<UsersState>(initialUsersState),
  withCallState({ collections: ['passwordUpdate', 'userStats'] }),
  withMethods(
    (
      store,
      userService = inject(UserService),
      snackbarService = inject(SnackbarService)
    ) => ({
      initialize() {
        store.loadTotalCountFromDB();
      },

      updatePassword(
        id: string,
        passwordData: {
          currentPassword: string;
          newPassword: string;
          newPasswordConfirmation: string;
        }
      ) {
        patchState(store, setLoading('passwordUpdate'));

        return userService.updatePassword(id, passwordData).subscribe({
          next: (response) => {
            snackbarService.show('Password updated successfully', 'success');

            patchState(store, setLoaded('passwordUpdate'));

            return response;
          },
          error: (error) => {
            const errorMessage =
              error.error?.message || 'Failed to update password';
            snackbarService.show(errorMessage, 'error');
            patchState(
              store,
              setError(errorMessage, 'passwordUpdate'),
              setLoaded('passwordUpdate')
            );
            throw error;
          },
        });
      },

      loadUserStatusStatistics() {
        patchState(store, setLoading('userStats'));

        userService.loadUserStatusStatistics().subscribe({
          next: (entity) => {
            patchState(
              store,
              {
                userStatusStatistics: entity,
              },
              setLoaded('userStats')
            );
          },
          error: (error) => {
            const errorMessage =
              error.error?.message || 'Failed to load user status statistics';
            snackbarService.show(errorMessage, 'error');
            console.error('Failed to load user status statistics', error);
            patchState(store, setError(errorMessage, 'userStats'));
          },
        });
      },

      loadUserSubscriptionStatistics() {
        patchState(store, setLoading('userStats'));

        userService.loadUserSubscriptionStatistics().subscribe({
          next: (entity) => {
            patchState(
              store,
              {
                userSubscriptionStatistics: entity,
              },
              setLoaded('userStats')
            );
          },
          error: (error) => {
            const errorMessage =
              error.error?.message ||
              'Failed to load user subscription statistics';
            snackbarService.show(errorMessage, 'error');
            console.error('Failed to load user subscription statistics', error);
            patchState(store, setError(errorMessage, 'userStats'));
          },
        });
      },
    })
  )
);
